/** Seconds in one hour (for google.protobuf.Duration JSON encoding). */
const SECONDS_PER_HOUR = 3600;

/** Seconds in one day (for google.protobuf.Duration JSON encoding). */
const SECONDS_PER_DAY = 86400;

export type TDurationPreset = '1h' | '24h' | '7d' | 'custom';

export type TDurationUnit = 'hours' | 'days';

export const MAX_DURATION_DAYS = 30;

export const MAX_DURATION_HOURS = MAX_DURATION_DAYS * 24;

export const DURATION_PRESETS: Exclude<TDurationPreset, 'custom'>[] = [
  '1h',
  '24h',
  '7d',
];

/**
 * Encodes a duration as google.protobuf.Duration in proto3 JSON form (`"{seconds}s`).
 *
 * @param totalSeconds - Whole seconds (must be positive)
 * @returns Duration string for JSON request bodies
 * @see https://protobuf.dev/reference/protobuf/google.protobuf/#json
 */
const toProtobufDurationJson = (totalSeconds: number): string =>
  `${Math.trunc(totalSeconds)}s`;

export const presetToTtl = (preset: TDurationPreset): string | null => {
  switch (preset) {
    case '1h':
      return toProtobufDurationJson(1 * SECONDS_PER_HOUR);
    case '24h':
      return toProtobufDurationJson(24 * SECONDS_PER_HOUR);
    case '7d':
      return toProtobufDurationJson(7 * SECONDS_PER_DAY);
    default:
      return null;
  }
};

export const customToTtl = (
  amount: number,
  unit: TDurationUnit,
): string | null => {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (unit === 'hours') {
    if (amount > MAX_DURATION_HOURS) return null;
    return toProtobufDurationJson(amount * SECONDS_PER_HOUR);
  }
  if (amount > MAX_DURATION_DAYS) return null;
  return toProtobufDurationJson(amount * SECONDS_PER_DAY);
};

export const buildTtl = (
  preset: TDurationPreset,
  customAmount: number | null,
  customUnit: TDurationUnit,
): string | null => {
  if (preset === 'custom') {
    if (customAmount == null) return null;
    return customToTtl(customAmount, customUnit);
  }
  return presetToTtl(preset);
};
